export type AssistantPresentationModule =
  | 'admissions'
  | 'financial-aid'
  | 'settings'
  | 'students-families'
  | 'tuition'
  | 'parent-portal'
  | 'general'
  | string;

export type AssistantPresentationDiagnostics = {
  reasonCode: 'machine_payload_sanitized' | 'machine_payload_blocked';
  title: string;
  summary: string;
  operatorGuidance?: string | null;
  module?: string | null;
  targetRoute?: string | null;
  displayMode?: 'icon' | 'drawer';
};

type ExtractedStructuredAssistantPayload = {
  answer?: string;
  citations?: unknown[];
  actions?: unknown[];
  debug?: Record<string, unknown>;
};

const MACHINE_PAYLOAD_KEY_PATTERN = /"(answer|citations|actions|debug|tenantStatus|dataSourceStatus|recommendation)"\s*:/i;
const MACHINE_PAYLOAD_LINE_PATTERN = /^\s*("?(answer|citations|actions|debug|tenantStatus|dataSourceStatus|recommendation)"?\s*:|[{}\[\]],?)\s*$/i;
const CODE_FENCE_PATTERN = /```(?:json|javascript|js|txt)?\s*([\s\S]*?)```/gi;
const JSON_FENCE_OPEN_PATTERN = /```(?:json|javascript|js|txt)?/gi;
const JSON_OBJECT_BODY_PATTERN = /\{[\s\S]*\}/;

const collapseWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

const collapseInlineWhitespace = (value: string): string => value.replace(/[^\S\n]+/g, ' ').trim();

const INLINE_MARKDOWN_HEADING_PATTERN = /([^\n])\s+(#{2,6}\s+)/g;
const MULTI_WORD_SECTION_HEADING_PATTERN = /([^\n])\s+([A-Z][A-Za-z0-9/&()'-]+(?: [A-Z][A-Za-z0-9/&()'-]+)+:)(?=\s+)/g;
const INLINE_PROMPT_HEADING_PATTERN = /([.!?])\s+(Please confirm:)(?=\s+)/g;
const INLINE_ORDERED_MARKER_PATTERN = /([^\n])\s+(\d+\.\s+)/g;
const INLINE_CHECKLIST_MARKER_PATTERN = /([^\n])\s+(-\s*\[[ xX]?\]\s+)/g;
const INLINE_DASH_BULLET_PATTERN = /([^\n])\s+(-\s+(?=[A-Z(]))/g;
const COLON_ORDERED_TRANSITION_PATTERN = /:\s+(?=\d+\.\s+)/g;
const COLON_DASH_TRANSITION_PATTERN = /:\s+(?=-\s+)/g;

const restoreStructuredLineBreaks = (value: string): string => {
  let next = String(value ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(/\u00A0/g, ' ')
    .replace(/[^\S\n]+/g, ' ');

  next = next.replace(INLINE_MARKDOWN_HEADING_PATTERN, '$1\n\n$2');
  next = next.replace(MULTI_WORD_SECTION_HEADING_PATTERN, '$1\n\n$2');
  next = next.replace(INLINE_PROMPT_HEADING_PATTERN, '$1\n\n$2');
  next = next.replace(COLON_ORDERED_TRANSITION_PATTERN, ':\n');
  next = next.replace(COLON_DASH_TRANSITION_PATTERN, ':\n');
  next = next.replace(INLINE_ORDERED_MARKER_PATTERN, '$1\n$2');
  next = next.replace(INLINE_CHECKLIST_MARKER_PATTERN, '$1\n$2');
  next = next.replace(INLINE_DASH_BULLET_PATTERN, '$1\n$2');

  return next;
};

const mergeOrphanedOrderedMarkers = (lines: string[]): string[] => {
  const merged: string[] = [];
  for (let index = 0; index < lines.length; index += 1) {
    const current = lines[index] ?? '';
    if (/^\d+\.$/.test(current)) {
      let nextIndex = index + 1;
      while (nextIndex < lines.length && !String(lines[nextIndex] ?? '').trim()) {
        nextIndex += 1;
      }
      const next = lines[nextIndex] ?? '';
      if (next && !/^\d+\.$/.test(next) && !/^[-*]\s/.test(next)) {
        merged.push(`${current} ${next}`.trim());
        index = nextIndex;
        continue;
      }
    }
    merged.push(current);
  }
  return merged;
};

const addOrderedListParagraphBreaks = (lines: string[]): string[] => {
  const next: string[] = [];
  for (const line of lines) {
    const current = String(line ?? "");
    const previous = next[next.length - 1] ?? "";
    const startsOrderedItem = /^\d+\.\s+/.test(current);
    const previousIsNonEmpty = Boolean(previous.trim());
    const previousIsOrderedItem = /^\d+\.\s+/.test(previous);
    const previousIsHeading = /^#{1,6}\s+/.test(previous.trim());
    const previousIntroducesList = previous.trim().endsWith(":");
    if (
      startsOrderedItem &&
      previousIsNonEmpty &&
      !previousIsOrderedItem &&
      !previousIsHeading &&
      !previousIntroducesList
    ) {
      next.push("");
    }
    next.push(current);
  }
  return next;
};

const normalizeAssistantDisplayText = (value: string): string => {
  const normalized = addOrderedListParagraphBreaks(
    mergeOrphanedOrderedMarkers(
      restoreStructuredLineBreaks(String(value ?? ""))
        .split("\n")
        .map((line) => collapseInlineWhitespace(line)),
    ),
  )
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return normalized;
};

const MARKDOWN_APP_ROUTE_LINK_PATTERN = /\[([^\]\n]+)\]\((?:file:\/\/)?(\/(?:school|parent\/portal)(?:\/[^)\s]+)?)\)/gi;
const BARE_APP_ROUTE_PATTERN = /(^|[\s([{"'`>])(?:file:\/\/)?\/(?:school|parent\/portal)(?:\/[A-Za-z0-9_%~:@!$&'*+,;=-]+)+(?![A-Za-z0-9_%~:@!$&'*+,;=-])/gi;
const UUID_LIKE_IDENTIFIER_PATTERN = /\b[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\b/gi;
const INTERNAL_ID_LABEL_PATTERN = /\b(Application|Family|Faculty|Note)\s+ID\b/gi;

const replaceInternalIdentifierLabel = (_match: string, entity: string): string => {
  switch (String(entity || '').toLowerCase()) {
    case 'family':
      return 'Family record';
    case 'faculty':
      return 'Faculty record';
    case 'note':
      return 'Note';
    case 'application':
    default:
      return 'Application';
  }
};

const redactDisplayOnlyRouteAndIdentifierLeakage = (value: string): string => (
  String(value ?? '')
    .replace(MARKDOWN_APP_ROUTE_LINK_PATTERN, (_match, label) => String(label ?? '').trim() || 'the relevant page')
    .replace(BARE_APP_ROUTE_PATTERN, (_match, prefix) => `${String(prefix ?? '')}the relevant page`)
    .replace(INTERNAL_ID_LABEL_PATTERN, replaceInternalIdentifierLabel)
    .replace(UUID_LIKE_IDENTIFIER_PATTERN, 'internal record')
);

const normalizeModule = (value: unknown): AssistantPresentationModule => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized || 'general';
};

const DEFAULT_MODULE_ROUTES: Record<string, string> = {
  admissions: '/school/admissions/dashboard',
  'financial-aid': '/school/financialaid/dashboard',
  faculty: '/school/faculty',
  settings: '/school/settings/general',
  'students-families': '/school/students/dashboard',
  tuition: '/school/tuition',
  'parent-portal': '/parent/portal/dashboard',
  general: '/school/admissions/dashboard',
};

const DEFAULT_MODULE_LABELS: Record<string, string> = {
  admissions: 'Admissions',
  'financial-aid': 'Financial Aid',
  faculty: 'Faculty',
  settings: 'Settings',
  'students-families': 'Students & Families',
  tuition: 'Tuition',
  'parent-portal': 'Parent Portal',
  general: 'Operations',
};

const parseJsonRecord = (value: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
};

const parseCandidatePayload = (value: string): ExtractedStructuredAssistantPayload | null => {
  const parsed = parseJsonRecord(value);
  if (!parsed) {
    return null;
  }
  if (!('answer' in parsed) && !('citations' in parsed) && !('actions' in parsed) && !('debug' in parsed)) {
    return null;
  }
  return {
    answer: typeof parsed.answer === 'string' ? parsed.answer : undefined,
    citations: Array.isArray(parsed.citations) ? parsed.citations : undefined,
    actions: Array.isArray(parsed.actions) ? parsed.actions : undefined,
    debug: parsed.debug && typeof parsed.debug === 'object' && !Array.isArray(parsed.debug)
      ? parsed.debug as Record<string, unknown>
      : undefined,
  };
};

const collectStructuredCandidates = (text: string): string[] => {
  const candidates = new Set<string>();
  const trimmed = String(text ?? '').trim();
  if (!trimmed) {
    return [];
  }
  candidates.add(trimmed);

  CODE_FENCE_PATTERN.lastIndex = 0;
  let match = CODE_FENCE_PATTERN.exec(trimmed);
  while (match) {
    const inner = String(match[1] ?? '').trim();
    if (inner) {
      candidates.add(inner);
    }
    match = CODE_FENCE_PATTERN.exec(trimmed);
  }

  const objectMatch = trimmed.match(JSON_OBJECT_BODY_PATTERN);
  if (objectMatch?.[0]) {
    candidates.add(objectMatch[0].trim());
  }

  return [...candidates];
};

export const extractStructuredAssistantPayload = (text: string): ExtractedStructuredAssistantPayload | null => {
  const candidates = collectStructuredCandidates(text);
  for (const candidate of candidates) {
    const parsed = parseCandidatePayload(candidate);
    if (parsed) {
      return parsed;
    }
  }
  return null;
};

export const looksLikeAssistantMachinePayload = (text: string): boolean => {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith('```')) {
    return true;
  }
  if (MACHINE_PAYLOAD_KEY_PATTERN.test(trimmed)) {
    return true;
  }
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    return true;
  }
  const lines = trimmed.split('\n').map((line) => line.trim()).filter(Boolean);
  if (lines.length > 0 && lines.every((line) => MACHINE_PAYLOAD_LINE_PATTERN.test(line) || /^".*"$/.test(line))) {
    return true;
  }
  return false;
};

export const stripAssistantMachinePayloadArtifacts = (text: string): string => {
  if (!text) {
    return '';
  }
  let next = String(text);
  next = next.replace(CODE_FENCE_PATTERN, (full, inner) => (
    looksLikeAssistantMachinePayload(String(inner ?? '')) ? '\n' : full
  ));
  next = next.replace(JSON_FENCE_OPEN_PATTERN, '');
  next = next
    .split('\n')
    .filter((line) => !MACHINE_PAYLOAD_LINE_PATTERN.test(line.trim()))
    .join('\n');
  next = next.replace(/\n{3,}/g, '\n\n').trim();
  return next;
};

const buildFallbackGuidance = (module: AssistantPresentationModule): {
  answer: string;
  diagnostics: AssistantPresentationDiagnostics;
} => {
  const normalizedModule = normalizeModule(module);
  const moduleLabel = DEFAULT_MODULE_LABELS[normalizedModule] ?? 'Assistant';
  const targetRoute = DEFAULT_MODULE_ROUTES[normalizedModule] ?? DEFAULT_MODULE_ROUTES.general;

  switch (normalizedModule) {
    case 'financial-aid':
      return {
        answer: 'I could not produce a clean live financial-aid answer for this request. Open the Financial Aid queue or retry the question for exact application links.',
        diagnostics: {
          reasonCode: 'machine_payload_blocked',
          title: 'Financial Aid answer unavailable',
          summary: 'I could not verify a usable live financial-aid answer from this session context.',
          operatorGuidance: 'Retry the exact-links request or open the Financial Aid queue for the current review set.',
          module: normalizedModule,
          targetRoute,
          displayMode: 'drawer',
        },
      };
    case 'settings':
      return {
        answer: 'I could not produce a clean settings answer for this request. Open the relevant settings page or retry with a narrower scope.',
        diagnostics: {
          reasonCode: 'machine_payload_blocked',
          title: 'Settings answer unavailable',
          summary: 'I could not verify a usable settings answer from this session context.',
          operatorGuidance: 'Retry from the relevant settings route with a narrower question.',
          module: normalizedModule,
          targetRoute,
          displayMode: 'drawer',
        },
      };
    case 'students-families':
      return {
        answer: 'I could not produce a clean student or family record answer for this request. Open the relevant record and retry from there.',
        diagnostics: {
          reasonCode: 'machine_payload_blocked',
          title: 'Student or family answer unavailable',
          summary: 'I could not verify a usable student or family answer from this session context.',
          operatorGuidance: 'Open the relevant family or student record and retry for focused detail.',
          module: normalizedModule,
          targetRoute,
          displayMode: 'drawer',
        },
      };
    case 'admissions':
      return {
        answer: 'I could not produce a clean admissions answer for this request. Open the admissions dashboard or retry with a narrower scope.',
        diagnostics: {
          reasonCode: 'machine_payload_blocked',
          title: 'Admissions answer unavailable',
          summary: 'I could not verify a usable admissions answer from this session context.',
          operatorGuidance: 'Retry the request on the current route or open the admissions dashboard for live context.',
          module: normalizedModule,
          targetRoute,
          displayMode: 'drawer',
        },
      };
    default:
      return {
        answer: `I could not produce a clean ${moduleLabel.toLowerCase()} answer for this request. Please retry or open the relevant route for live context.`,
        diagnostics: {
          reasonCode: 'machine_payload_blocked',
          title: `${moduleLabel} answer unavailable`,
          summary: 'I could not verify a usable answer from this session context.',
          operatorGuidance: 'Retry the request on a route with live context if the answer still looks incomplete.',
          module: normalizedModule,
          targetRoute,
          displayMode: 'drawer',
        },
      };
  }
};

export const sanitizeAssistantAnswerForDisplay = (input: {
  answer: string;
  module?: AssistantPresentationModule | null;
  originalAnswer?: string | null;
}): {
  answer: string;
  diagnostics: AssistantPresentationDiagnostics | null;
  sanitized: boolean;
  extractedPayload: ExtractedStructuredAssistantPayload | null;
} => {
  const extractedPayload = extractStructuredAssistantPayload(input.answer);
  const rawCandidateAnswer = normalizeAssistantDisplayText(
    extractedPayload?.answer
    ?? stripAssistantMachinePayloadArtifacts(input.answer)
  );
  const candidateAnswer = normalizeAssistantDisplayText(
    redactDisplayOnlyRouteAndIdentifierLeakage(rawCandidateAnswer)
  );

  if (candidateAnswer && !looksLikeAssistantMachinePayload(candidateAnswer)) {
    const sanitized = normalizeAssistantDisplayText(String(input.originalAnswer ?? input.answer ?? '')) !== candidateAnswer;
    return {
      answer: candidateAnswer,
      diagnostics: sanitized
        ? {
            reasonCode: 'machine_payload_sanitized',
            title: 'Display note',
            summary: 'A cleaned summary was shown because the assistant returned machine-shaped content.',
            operatorGuidance: 'If the answer still looks incomplete, retry from a route with live context.',
            module: normalizeModule(input.module ?? 'general'),
            targetRoute: DEFAULT_MODULE_ROUTES[normalizeModule(input.module ?? 'general')] ?? DEFAULT_MODULE_ROUTES.general,
            displayMode: 'icon',
          }
        : null,
      sanitized,
      extractedPayload,
    };
  }

  const fallback = buildFallbackGuidance(normalizeModule(input.module ?? 'general'));
  return {
    answer: fallback.answer,
    diagnostics: fallback.diagnostics,
    sanitized: true,
    extractedPayload,
  };
};
